import { Button, ButtonThemesEnum } from '@propertyfinder/button';
import { DrawerAnimationDirectionEnum, DrawerComponent } from '@propertyfinder/drawer';
import { IconBin } from '@propertyfinder/icons';
import { ModalComponent } from '@propertyfinder/modal';
import { TabsComponent } from '@propertyfinder/tabs';
import { Fragment, h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';

import { saveSearchFilterMapper } from 'common/data/save-search/filter-mapper';
import { SaveSearchFrequencyEnum } from 'common/data/save-search/frequency.enum';
import { SaveSearchModel } from 'common/data/save-search/model';
import { SaveSearchStoreEvent } from 'common/data/save-search/store.event';
import { createAlertEnabled } from 'common/features/create-alert/enabled';
import { abTestsIsVariant } from 'common/helper/ab-tests/is-variant';
import { configGetEnv } from 'common/helper/config/get-env';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { ButtonComponent } from 'common/module/button/component';
import { CreateAlertCardComponent } from 'common/module/create-alert/card/component';
import { FilterParamsEnum } from 'common/module/filter/params.enum';
import { IconEmptyState, IconNotLoggedInState } from 'common/module/icon';
import { MyAccountEmptyStateTemplate } from 'common/module/my-account/empty-state/template';
import { SaveSearchListRenameDrawerComponent } from 'common/module/save-search/list/rename-drawer.component';
import { SaveSearchListRenameModalComponent } from 'common/module/save-search/list/rename-modal.component';
import { StatsTealiumDataLayerEventReferenceEnum } from 'common/module/stats/tealium/data-layer/event-reference.enum';
import { UserAuthAsyncComponent } from 'common/module/user/auth/async.component';
import { UserAuthDrawerAsyncComponent } from 'common/module/user/auth/async-drawer.component';
import { MyAccountStoreService } from 'common/service/my-account-store/service';
import { SaveSearchStoreService } from 'common/service/save-search-store/service';

import { SaveSearchCardComponent } from '../card/component';
import { SaveSearchEmptyStateTemplate } from '../empty-state/template';
import { SaveSearchFormFieldsInterface } from '../form/fields.interface';
import { SaveSearchListComponentFactoryPropsInterface } from './component-factory-props.interface';

export const SaveSearchListComponentFactory =
  ({
    userAuthenticationService,
    saveSearchStoreService,
    filterSearchAdapter,
    messageService,
    getSearchUri,
    formComponent: FormComponent,
    saveSearchTracker,
    windowLocationService,
  }: SaveSearchListComponentFactoryPropsInterface) =>
  (): h.JSX.Element => {
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(!!userAuthenticationService.getToken());
    const [renameModal, setRenameModal] = useState<{
      isVisible: boolean;
      saveSearchModel?: SaveSearchModel;
    }>({
      isVisible: false,
    });
    const userModel = userAuthenticationService.getUser();
    const [actionInProgress, setActionInProgress] = useState(false);
    const [searchModelForEdit, setSearchModelForEdit] = useState<SaveSearchFormFieldsInterface>(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [confirmModal, setConfirmModal] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState({ id: '', error: '' });
    const [searchList, setSearchList] = useState<SaveSearchModel[]>([]);

    const isAlertExperimentEnabled = useMemo(
      () => createAlertEnabled() && abTestsIsVariant('SEARCH_CREATE_ALERT_FLOW', 'variantA'),
      []
    );
    const undoDeletedCard = (searchModel: SaveSearchModel): void => {
      saveSearchStoreService.updateFrequency(searchModel.id, searchModel.frequency).then(() => {
        messageService.updateVisibility(false);
        saveSearchStoreService.getAll(true).then();
      });
    };

    const deleteSavedSearch = (searchModel: SaveSearchModel): void => {
      if (actionInProgress) {
        return;
      }
      setActionInProgress(true);
      saveSearchStoreService
        .deleteSingle(searchModel.id)
        .then(() => {
          messageService.alert({
            message: i18nTranslate('save_search:snackbar-deleted-card'),
            action: <button onClick={(): void => undoDeletedCard(searchModel)}>{i18nTranslate('global:undo')}</button>,
          });

          saveSearchTracker.onDeleteSuccess(searchModel.id);
        })
        .finally(() => setActionInProgress(false));
    };

    const navigateSavedSearch = (searchModel: SaveSearchModel): void => {
      const filterParams = saveSearchFilterMapper(searchModel.filters);
      saveSearchTracker.onClickCard(filterParams?.[FilterParamsEnum.locationsIds]?.value);
      filterSearchAdapter.redirect(getSearchUri(), filterParams);
    };

    useEffect(() => {
      const savedSearchStoreEventEmitter = saveSearchStoreService.getEventEmitter();

      const onFetchSavedSearchSuccess = (savedSearches: SaveSearchModel[]): void => {
        setSearchList(savedSearches);
      };

      const onDeleteAllSavedSearches = (): void => {
        saveSearchStoreService.deleteAll().then(() => {
          saveSearchTracker.onDeleteAllSuccess();
          messageService.alert({
            message: i18nTranslate('save_search:snackbar-deleted-cards'),
          });
        });
      };

      savedSearchStoreEventEmitter.addListener(SaveSearchStoreEvent.requestDeleteAll, onDeleteAllSavedSearches);
      savedSearchStoreEventEmitter.addListener(SaveSearchStoreEvent.searchSuccess, onFetchSavedSearchSuccess);
      return (): void => {
        savedSearchStoreEventEmitter.removeListener(SaveSearchStoreEvent.searchSuccess, onFetchSavedSearchSuccess);
        savedSearchStoreEventEmitter.removeListener(SaveSearchStoreEvent.requestDeleteAll, onDeleteAllSavedSearches);
      };
    }, [isUserLoggedIn]);

    const undoDeletedSavedSearch = (searchModel: SaveSearchModel): void => {
      setIsDisabled(true);
      messageService.updateVisibility(false);
      saveSearchStoreService.reCreate(searchModel.name, searchModel.frequency, searchModel.filters).then(() => {
        saveSearchStoreService.getAll(true).then(() => setIsDisabled(false));
      });
    };

    const SaveSearchTabContent = useMemo(() => {
      return (): any => {
        if (!userAuthenticationService.getToken()) {
          return isAlertExperimentEnabled ? (
            <Fragment>
              <SaveSearchEmptyStateTemplate
                icon={
                  <img
                    src={__('assets/common/save-search.svg')}
                    className='save-search__empty-state__icon-image'
                    alt={'create alert'}
                  />
                }
                title={i18nTranslate('CreateAlert_EmptyStateNotLoggedInTitle')}
                description={i18nTranslate('CreateAlert_EmptyStateDescription')}
                buttonText={i18nTranslate('CreateAlert_EmptyStateNotLoggedInButton')}
                onClick={(): void => setModalVisible(true)}
              />

              {modalVisible && configGetEnv().platform === 'desktop' && (
                <UserAuthAsyncComponent
                  reference={StatsTealiumDataLayerEventReferenceEnum.SAVED_SEARCH}
                  customModalTitle={i18nTranslate('ManageAlert_LoginModalTitle')}
                  customModalDescription={i18nTranslate('ManageAlert_LoginModalDescription')}
                  customModalImage={__('assets/common/save-search.svg')}
                  onSuccess={(): void => {
                    setModalVisible(false);
                    setIsUserLoggedIn(true);
                    saveSearchStoreService.getAll(true).then();
                  }}
                  onClose={(): void => setModalVisible(false)}
                />
              )}

              {modalVisible && configGetEnv().platform === 'mobile' && (
                <UserAuthDrawerAsyncComponent
                  reference={StatsTealiumDataLayerEventReferenceEnum.SAVED_SEARCH}
                  customModalTitle={i18nTranslate('ManageAlert_LoginModalTitle')}
                  customModalDescription={i18nTranslate('ManageAlert_LoginModalDescription')}
                  customModalImage={__('assets/common/save-search.svg')}
                  onSuccess={(): void => {
                    setModalVisible(false);
                    setIsUserLoggedIn(true);
                    saveSearchStoreService.getAll(true).then();
                  }}
                  onClose={(): void => setModalVisible(false)}
                />
              )}
            </Fragment>
          ) : (
            <MyAccountEmptyStateTemplate
              icon={<IconNotLoggedInState />}
              title={i18nTranslate('save_search:not-logged-in-title')}
              description={i18nTranslate('save_search:not-logged-in-description')}
            />
          );
        } else if (!searchList.length) {
          return isAlertExperimentEnabled ? (
            <SaveSearchEmptyStateTemplate
              icon={
                <img
                  src={__('assets/common/save-search.svg')}
                  className='save-search__empty-state__icon-image'
                  alt={'create alert'}
                />
              }
              title={i18nTranslate('CreateAlert_EmptyStateLoggedInTitle')}
              description={i18nTranslate('CreateAlert_EmptyStateDescription')}
              buttonText={i18nTranslate('CreateAlert_EmptyStateLoggedInButton')}
              onClick={(): void => {
                MyAccountStoreService().onCreateAlertClicked();
                windowLocationService.redirect(configGetEnv().userPropertySearchUrl, {
                  hash: '',
                  queryData: {},
                });
              }}
            />
          ) : (
            <MyAccountEmptyStateTemplate
              icon={<IconEmptyState />}
              title={i18nTranslate('save_search:empty-list-title')}
              description={i18nTranslate('save_search:empty-list-description')}
            >
              <a href={getSearchUri()} className='button-2 button-primary saved-search-list__empty-action'>
                {i18nTranslate('save_search:empty-list-action')}
              </a>
            </MyAccountEmptyStateTemplate>
          );
        } else if (searchList.length) {
          return searchList.map((savedSearch, index) => {
            return isAlertExperimentEnabled ? (
              <Fragment key={index}>
                <CreateAlertCardComponent
                  key={index}
                  saveSearchModel={savedSearch}
                  error={errorMessage}
                  onCardClicked={(): void => navigateSavedSearch(savedSearch)}
                  onActionsClicked={(): void => MyAccountStoreService().onActionsClicked()}
                  onRenameClicked={(saveSearchModel: SaveSearchModel): void =>
                    setRenameModal({ isVisible: true, saveSearchModel })
                  }
                  onDeleteClicked={(searchModel: SaveSearchModel): void => {
                    saveSearchStoreService.deleteSingle(searchModel.id).then((): void => {
                      messageService.alert({
                        message: i18nTranslate('ManageAlert_DeleteMessage'),
                        action: (
                          <button onClick={(): void => undoDeletedSavedSearch(searchModel)} disabled={isDisabled}>
                            {i18nTranslate('global:undo')}
                          </button>
                        ),
                      });
                    });
                  }}
                  onFrequencyChanged={(selectedItem: SaveSearchFrequencyEnum): void => {
                    SaveSearchStoreService()
                      .update(savedSearch.id, {
                        name: savedSearch.name,
                        frequency: selectedItem,
                      })
                      .then((response) => {
                        messageService.alert({
                          message: i18nTranslate('ManageAlert_RenameMessage'),
                        });
                        return response;
                      })
                      .catch((): void => {
                        setErrorMessage({
                          id: savedSearch.id,
                          error: i18nTranslate('Something went wrong! Please try again later'),
                        });
                      });
                  }}
                >
                  {savedSearch.formatted_filters}
                </CreateAlertCardComponent>

                {index === searchList.length - 1 && (
                  <Fragment>
                    <Button
                      theme={ButtonThemesEnum.GHOST}
                      className='delete-all_button'
                      onClick={(): void => setConfirmModal(true)}
                      icon={{ component: IconBin, position: 'prefix' }}
                    >
                      {i18nTranslate('Delete all')}
                    </Button>

                    {confirmModal && configGetEnv().platform === 'desktop' && (
                      <ModalComponent
                        openOnLoad
                        showFooter={true}
                        bodyClassName={'authentication-v2__body'}
                        contentClassName={'confirmation__drawer--content'}
                        footerClassName={'confirmation__drawer--footer'}
                        title={i18nTranslate('ManageAlert_DeleteAllModalHeader')}
                        onClose={(): void => setConfirmModal(false)}
                        primaryAction={{
                          content: i18nTranslate('Delete all'),
                          props: {
                            className: 'confirmation__drawer--primary-button',
                            onClick: (): Promise<void> =>
                              saveSearchStoreService.deleteAll().then((): void => {
                                setConfirmModal(false);
                                messageService.alert({
                                  message: i18nTranslate('ManageAlert_DeleteAllMessage'),
                                });
                              }),
                          },
                        }}
                        secondaryAction={{
                          content: i18nTranslate('Cancel'),
                          props: {
                            onClick: (): void => setConfirmModal(false),
                          },
                        }}
                      >
                        {i18nTranslate('ManageAlert_DeleteAllModalContent')}
                      </ModalComponent>
                    )}

                    {confirmModal && configGetEnv().platform === 'mobile' && (
                      <DrawerComponent
                        onClose={(): void => setConfirmModal(false)}
                        openOnLoad={true}
                        className={'confirmation__drawer'}
                        headerClassName={'confirmation__drawer--header'}
                        contentClassName={'confirmation__drawer--content'}
                        footerClassName={'confirmation__drawer--footer'}
                        showCloseButton={false}
                        openingDirection={DrawerAnimationDirectionEnum.UP}
                        header={<div>{i18nTranslate('ManageAlert_DeleteAllModalHeader')}</div>}
                        footer={
                          <Fragment>
                            <Button
                              theme={ButtonThemesEnum.LINK}
                              type={'button'}
                              onClick={(): void => setConfirmModal(false)}
                            >
                              {i18nTranslate('Cancel')}
                            </Button>
                            <Button
                              theme={ButtonThemesEnum.PRIMARY}
                              type={'button'}
                              onClick={(): void => {
                                saveSearchStoreService.deleteAll().then((): void => {
                                  setConfirmModal(false);
                                  messageService.alert({
                                    message: i18nTranslate('ManageAlert_DeleteAllMessage'),
                                  });
                                });
                              }}
                            >
                              {i18nTranslate('Delete all')}
                            </Button>
                          </Fragment>
                        }
                      >
                        {i18nTranslate('ManageAlert_DeleteAllModalContent')}
                      </DrawerComponent>
                    )}

                    {renameModal.isVisible && configGetEnv().platform === 'desktop' && (
                      <SaveSearchListRenameModalComponent
                        saveSearchModel={renameModal.saveSearchModel}
                        closeModal={(): void => setRenameModal({ isVisible: false })}
                        messageService={messageService}
                      />
                    )}
                  </Fragment>
                )}
              </Fragment>
            ) : (
              <Fragment key={index}>
                <SaveSearchCardComponent
                  key={index}
                  className='saved-search-list__card'
                  title={savedSearch.name}
                  list={[
                    {
                      label: i18nTranslate('save_search:frequency:label'),
                      value: i18nTranslate(savedSearch.frequency),
                    },
                  ]}
                  action={
                    <Fragment>
                      <ButtonComponent onClick={(): void => navigateSavedSearch(savedSearch)}>
                        {i18nTranslate('View')}
                      </ButtonComponent>
                      <ButtonComponent
                        onClick={(): void => {
                          setSearchModelForEdit(savedSearch);
                          saveSearchTracker.onOpenEditDialog();
                        }}
                      >
                        {i18nTranslate('Edit')}
                      </ButtonComponent>
                      <ButtonComponent onClick={(): void => deleteSavedSearch(savedSearch)}>
                        {i18nTranslate('Delete')}
                      </ButtonComponent>
                    </Fragment>
                  }
                >
                  {savedSearch.formatted_filters}
                </SaveSearchCardComponent>

                {!!searchModelForEdit && (
                  <FormComponent
                    title={i18nTranslate('save_search:form:edit:title')}
                    userHasEmail={!!userModel?.email}
                    fields={searchModelForEdit}
                    onClose={(): void => setSearchModelForEdit(null)}
                    onCancel={(): void => setSearchModelForEdit(null)}
                    onSuccess={(): void => setSearchModelForEdit(null)}
                  />
                )}
              </Fragment>
            );
          });
        }
      };
    }, [searchList, userModel, searchModelForEdit, modalVisible, confirmModal, errorMessage, renameModal]);

    const TabComponentContent = useMemo(() => {
      return (): any => {
        return (
          <TabsComponent
            className={'save_searches_tabs'}
            activeTabIndex={1}
            items={[
              {
                title: i18nTranslate('SavedProperties_TabTitle'),
                content: <div className={'tab-content'} />,
              },
              {
                title: i18nTranslate('CreateAlert_TabTitle'),
                content: (
                  <Fragment>
                    <SaveSearchTabContent />

                    {renameModal.isVisible && configGetEnv().platform === 'mobile' && (
                      <SaveSearchListRenameDrawerComponent
                        saveSearchModel={renameModal.saveSearchModel}
                        closeModal={(): void => setRenameModal({ isVisible: false })}
                        messageService={messageService}
                      />
                    )}
                  </Fragment>
                ),
              },
            ]}
            onTabChange={(tabNum): void => {
              if (tabNum === 0) {
                windowLocationService.redirect(configGetEnv().userAccountSavedPropertiesUrl, {
                  hash: '',
                  queryData: {},
                });
              }
            }}
          />
        );
      };
    }, [searchList, userModel, searchModelForEdit, modalVisible, renameModal, confirmModal]);

    return isAlertExperimentEnabled && configGetEnv().platform === 'mobile' ? (
      <TabComponentContent />
    ) : (
      <SaveSearchTabContent />
    );
  };
